import { useEffect, useState } from "react";
import { Store, ArrowUpRight, Brain, BarChart3, Building2, HeartHandshake, Settings, LifeBuoy } from "lucide-react";
import api, { inr } from "../api";

const COLUMNS = {
  followups: [["patient", "Patient"], ["treatment", "Treatment"], ["due_date", "Due"], ["status", "Status"]],
  vendors: [["name", "Vendor"], ["category", "Category"], ["contact", "Contact"], ["rating", "Rating"]],
  procurement: [["order", "Order"], ["vendor", "Vendor"], ["items", "Items"], ["amount", "Amount"], ["status", "Status"]],
  staff: [["name", "Name"], ["role", "Role"], ["shift", "Shift"], ["present", "Present"]],
  equipment: [["name", "Equipment"], ["model", "Model"], ["status", "Status"], ["last_service", "Last Service"]],
};

export function ListModule({ module, title }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/connect/list/${module}`).then((r) => setItems(r.data)).catch(() => {}); }, [module]);
  const cols = COLUMNS[module];

  return (
    <div data-testid={`connect-${module}`} className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-semibold text-pine-deep">{title}</h2>
        <p className="text-sm text-slate-500">{items.length} records.</p>
      </div>
      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[620px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                {cols.map(([k, label]) => <th key={k} className="px-5 py-3.5 font-bold">{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-pine/5 last:border-0 hover:bg-cream/50">
                  {cols.map(([k]) => (
                    <td key={k} className="px-5 py-3.5 text-slate-600 first:font-bold first:text-pine-deep">
                      {k === "amount" ? inr(row[k]) : k === "present" ? (row[k] ? "Present" : "On Leave") : String(row[k] ?? "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const STATIC_CONTENT = {
  CRM: { icon: HeartHandshake, points: ["Patient communication history", "Recall & re-engagement lists", "Feedback collection", "Loyalty tracking"] },
  "Reports & Analytics": { icon: BarChart3, points: ["Revenue by treatment & doctor", "Patient growth trends", "Chair utilization", "Expense & profitability reports"] },
  "Multi-Clinic": { icon: Building2, points: ["Switch between clinic locations", "Consolidated network reports", "Compare clinic performance", "Centralized inventory view"] },
  "AI Insights": { icon: Brain, points: ["Revenue prediction", "Patient retention scoring", "Inventory forecasting", "Growth suggestions"] },
  Settings: { icon: Settings, points: ["Clinic profile & hours", "Team roles & access", "Notification preferences", "Website booking preferences"] },
  "Help & Support": { icon: LifeBuoy, points: ["Guides & tutorials", "Chat with Dyntra support", "WhatsApp: +91 9801910117", "Support & bookings: info@dyntraclinics.com", "Ecosystem: info@dyntra.store"] },
};

export function StaticModule({ title, store }) {
  const c = STATIC_CONTENT[title];
  if (store) {
    return (
      <div data-testid="connect-store" className="rounded-3xl bg-pine-deep text-ivory p-10 relative overflow-hidden grain max-w-3xl">
        <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-gold">Dyntra Store</p>
        <h2 className="mt-3 font-display text-3xl font-semibold">Clinic procurement, one click away.</h2>
        <p className="mt-3 text-sm text-ivory/70 max-w-md">Order dental equipment, consumables and clinic supplies directly from the Dyntra Store ecosystem — preferred pricing for Dyntra Clinics partners.</p>
        <a data-testid="store-open-link" href="https://dyntra.store" target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-bold text-pine-deep hover:bg-gold-soft transition-colors">
          <Store className="h-4 w-4" /> Open Dyntra Store <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    );
  }
  const Icon = c?.icon || Settings;
  return (
    <div data-testid={`connect-static-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="max-w-3xl space-y-5">
      <div className="rounded-3xl bg-white border border-pine/8 shadow-soft p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-soft text-teal-brand"><Icon className="h-6 w-6" strokeWidth={1.8} /></span>
        <h2 className="mt-4 font-display text-2xl font-semibold text-pine-deep">{title}</h2>
        <p className="mt-2 text-sm text-slate-500">This module is being rolled out for your clinic. Here's what's coming:</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
          {(c?.points || []).map((p) => (
            <li key={p} className="flex items-center gap-2.5 rounded-xl bg-cream/70 px-4 py-3 text-sm font-semibold text-pine-deep">{p}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
